import { createAsyncThunk } from '@reduxjs/toolkit';
import { getErrorTypeFromApi } from '../../utils/ErrorHandling/errorUtil';
import { LoginSteps } from './login.constants';
import { getPasskeyAuthenticationErrorType } from './passkeySupport';
import { startAuthentication } from '@simplewebauthn/browser';
import {
  loginWithPasskeyAPI,
  loginWithPasswordAPI,
  loginWithTotpAPI,
  loginWithPasskey2faAPI,
  generatePasskeyAuthenticationOptionsAPI,
  generate2faPasskeyAuthenticationOptionsAPI,
} from '../../API/services';

export const loginWithPassword = createAsyncThunk(
  'user/loginWithPassword',
  async (credentials, { rejectWithValue }) => {
    try {
      const loginWithPasswordResponse = await loginWithPasswordAPI(credentials);

      if (loginWithPasswordResponse.status !== 200) {
        return rejectWithValue(getErrorTypeFromApi(loginWithPasswordResponse));
      }

      const data = loginWithPasswordResponse.data?.data || {};

      if (!data.requiresTwoFactor) {
        return data;
      }

      return { ...data, step: LoginSteps.TWO_FACTOR_AUTHENTICATION };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const loginWithPasskey = createAsyncThunk(
  'user/loginWithPasskey',
  async (_, { rejectWithValue }) => {
    try {
      // Generate passkey authentication options from the API
      const authenticationOptionsResponse =
        await generatePasskeyAuthenticationOptionsAPI({}, {}, {});

      if (authenticationOptionsResponse.status !== 200) {
        return rejectWithValue(
          getErrorTypeFromApi(authenticationOptionsResponse),
        );
      }
      const optionsJSON = authenticationOptionsResponse.data.data.options;
      const challengeId = authenticationOptionsResponse.data.data.challengeId;

      // Start the passkey authentication process using the generated options
      let asseResp;
      try {
        asseResp = await startAuthentication({ optionsJSON });
      } catch (error) {
        return rejectWithValue(await getPasskeyAuthenticationErrorType(error));
      }

      // Send the authentication response along with the challenge ID to the API for verification
      const loginWithPasskeyResponse = await loginWithPasskeyAPI({
        challengeId,
        asseResp,
      });

      if (loginWithPasskeyResponse.status !== 200) {
        return rejectWithValue(getErrorTypeFromApi(loginWithPasskeyResponse));
      }

      const data = loginWithPasskeyResponse.data?.data || {};
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Second factor after the password: only the passkeys of the user bound to the twoFactorToken are offered
export const loginWithPasskey2fa = createAsyncThunk(
  'user/loginWithPasskey2fa',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { twoFactorToken } = getState().login;

      const authenticationOptionsResponse =
        await generate2faPasskeyAuthenticationOptionsAPI({ twoFactorToken });

      if (authenticationOptionsResponse.status !== 200) {
        return rejectWithValue(
          getErrorTypeFromApi(authenticationOptionsResponse),
        );
      }
      const optionsJSON = authenticationOptionsResponse.data.data.options;
      const challengeId = authenticationOptionsResponse.data.data.challengeId;

      let asseResp;
      try {
        asseResp = await startAuthentication({ optionsJSON });
      } catch (error) {
        return rejectWithValue(await getPasskeyAuthenticationErrorType(error));
      }

      const loginWithPasskey2faResponse = await loginWithPasskey2faAPI({
        twoFactorToken,
        challengeId,
        asseResp,
      });

      if (loginWithPasskey2faResponse.status !== 200) {
        return rejectWithValue(
          getErrorTypeFromApi(loginWithPasskey2faResponse),
        );
      }

      const data = loginWithPasskey2faResponse.data?.data || {};
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const loginWithTotp = createAsyncThunk(
  'user/loginWithTotp',
  async (credentials, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const loginWithTotpResponse = await loginWithTotpAPI({
        ...credentials,
        twoFactorToken: state.login.twoFactorToken,
      });

      if (loginWithTotpResponse.status !== 200) {
        return rejectWithValue(getErrorTypeFromApi(loginWithTotpResponse));
      }

      const data = loginWithTotpResponse.data?.data || {};
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);
